import {
	CardNumberElement,
	useElements,
	useStripe,
} from '@stripe/react-stripe-js';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { API_URL } from 'constant';
import { useNetwork, useNotification } from 'hooks';
import {
	AddCardModalState,
	PaymentNavigationState,
} from 'views/settings/billing/stores';
import {
	IndetityFlowNavigate,
	SubscriptionIdentityNaviagtion,
	useDefaultCard,
} from 'views/user-identity-flow';
import { useIsSv2Dark } from 'views/design-system/use-sv2-dark';
import { PaymentNavigation } from '../../constant';

interface IComplete {
	cardNumber: boolean;
	cvv: boolean;
	expiryDate: boolean;
}

type UseAddCard = {
	from?: 'SIGNUP' | 'HOME';
};
export const useAddCard = ({ from }: UseAddCard) => {
	const setAddPaymentNaviagtion = useSetRecoilState(PaymentNavigationState);
	const { isEdit } = useRecoilValue(AddCardModalState);
	const setNavigate = useSetRecoilState(SubscriptionIdentityNaviagtion);
	const stripe = useStripe();
	const elements = useElements();
	const { successNotification, errorNotification } = useNotification();
	const { post: saveCard } = useNetwork({ returnResponse: true });

	const [isLoader, setIsLoader] = useState(false);
	const [defaultCheck, setDefaultCheck] = useState(false);

	const [complete, setComplete] = useState<IComplete>({
		cardNumber: false,
		cvv: false,
		expiryDate: false,
	});

	const handleChangeCard = useCallback((isComplete: boolean, name: string) => {
		setComplete(prev => ({ ...prev, [name]: isComplete }));
	}, []);

	const manageBtnDisabled = useMemo((): boolean => {
		const { cardNumber, cvv, expiryDate } = complete;
		if (cardNumber && cvv && expiryDate && stripe) {
			return false;
		}
		return true;
	}, [complete, stripe]);

	/**
	 * STRIPE ELEMENTS ARE CROSS-ORIGIN IFRAMES, so none of our CSS reaches inside
	 * them: not a stylesheet, not a `.sv2-scope` token, not a CSS variable. Every
	 * colour has to be handed over as a literal in this options object, and handed
	 * over AGAIN when the theme changes — which is why this reads the theme in JS
	 * rather than leaving it to the cascade.
	 *
	 * Two things were wrong before. The colours were fixed light-mode values
	 * (`#424770` on `#0d1117`), and `invalid` was `var(--color-danger-dark)` — a
	 * variable Stripe's frame cannot resolve, so invalid input was never coloured
	 * at all. `color` also drives the CARET inside the frame, which is what made
	 * the cursor almost invisible: clicking the field looked like nothing
	 * happened.
	 */
	const isSv2Dark = useIsSv2Dark();

	const options = useMemo(
		() => ({
			style: {
				base: {
					color: isSv2Dark ? '#e6edf3' : '#424770',
					letterSpacing: '0.025em',
					fontFamily: 'Source Code Pro, monospace',
					'::placeholder': {
						color: isSv2Dark ? '#8b949e' : '#aab7c4',
					},
				},
				invalid: {
					/* Literal, not a var: see above. */
					color: isSv2Dark ? '#ff7b72' : '#df1b41',
				},
			},
		}),
		[isSv2Dark]
	);
	const { isDefaultExist } = useDefaultCard();

	// only first time mount
	useEffect(() => {
		if (!isDefaultExist) {
			setDefaultCheck(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDefaultExist]);

	/**
	 * BACK MUST MOVE THE FLOW THIS FORM IS MOUNTED IN.
	 *
	 * The form serves two step machines: the sign-up identity flow
	 * (`from === 'SIGNUP'`, `SubscriptionIdentityNaviagtion`) and the Billing
	 * page's payment-method step (`PaymentNavigationState`). With a default
	 * card already on file and no edit in progress, Back used to write the
	 * SIGN-UP atom even on Billing — so Billing's step never changed, the
	 * Card / ACH / Wire choice never came back, and the page was a dead end
	 * (SIM-45). Each branch now writes only the flow it belongs to.
	 */
	const handleCloseViewModal = () => {
		if (from === 'SIGNUP') {
			setNavigate(
				isDefaultExist
					? IndetityFlowNavigate.CardView
					: IndetityFlowNavigate.ChooseMethod
			);
			return;
		}

		if (isDefaultExist && isEdit) {
			setAddPaymentNaviagtion(PaymentNavigation.CardView);
			return;
		}

		// ActivePaymentType is left alone, so the choice the user made is still
		// selected when the options come back.
		setAddPaymentNaviagtion(PaymentNavigation.ChoosePage);
	};

	const onSubmitCard = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!stripe || !elements) {
			return;
		}

		setIsLoader(true);
		const tokenDetails: any = await stripe.createToken(
			elements.getElement(CardNumberElement) as any
		);

		if (tokenDetails.error) {
			setIsLoader(false);
			return errorNotification(tokenDetails.error.message);
		}
		if (!tokenDetails) {
			return errorNotification('Invalid Card');
		}

		saveCard(API_URL.BUSINESS_CARD, {
			token: tokenDetails.token.id,
			default: defaultCheck,
		}).then(resp => {
			const { errorCode, errorData } = resp;
			if (errorCode) {
				errorNotification(errorData.message);
				setIsLoader(false);
				return;
			}
			if (resp.id) {
				successNotification('Card Saved Successfully!');
				if (setNavigate && from === 'SIGNUP')
					setNavigate(IndetityFlowNavigate.SubscriptionReview);
				else setAddPaymentNaviagtion(PaymentNavigation.Processing);
			}
			setIsLoader(false);
		});

		return null;
	};

	const handleChangeDefaultCheck = (e: ChangeEvent<HTMLInputElement>) => {
		if (!isDefaultExist) return;
		setDefaultCheck(e.target.checked);
	};

	const cvvConfigOptions = {
		...options,
		placeholder: '...',
	};

	return {
		onSubmitCard,
		handleChangeCard,
		/* Card number and expiry were rendered with NO options at all, so they fell
		   back to Stripe's own defaults (a near-black `#32325d`) regardless of
		   theme. Only the CVV was ever styled, which is exactly why it was the one
		   field whose caret and text stayed readable. */
		cardConfigOptions: options,
		cvvConfigOptions,
		handleChangeDefaultCheck,
		defaultCheck,
		handleCloseViewModal,
		isLoader,
		manageBtnDisabled,
	};
};
